import { motion } from 'framer-motion'
import { CalendarDays, LayoutGrid, Users } from 'lucide-react'

import { DashboardDonut, DashboardDonutLegend } from '@/components/dashboard/DashboardDonut'
import { useCommandDashboard } from '@/contexts/CommandDashboardContext'
import {
  appointmentMixChartData,
  floorMixChartData,
  guestMixChartData,
} from '@/lib/dashboard/dashboardChartData'
import { cn } from '@/lib/utils'

/**
 * @param {{ title: string, icon: import('lucide-react').LucideIcon, children: import('react').ReactNode, className?: string }} props
 */
function InsightChartCard({ title, icon: Icon, children, className }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn('card-surface flex flex-col p-4 lg:p-5', className)}
    >
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-4 w-4" strokeWidth={2.25} />
        </div>
        <h3 className="text-sm font-bold tracking-tight text-foreground">{title}</h3>
      </div>
      {children}
    </motion.article>
  )
}

export function OwnerInsightsCharts() {
  const { data } = useCommandDashboard()
  const guestData = guestMixChartData(data?.snapshot)
  const floorData = floorMixChartData(data?.floor)
  const apptData = appointmentMixChartData(data?.timeline)
  const occupancy = data?.floor?.stats?.occupancy_pct ?? 0

  return (
    <section
      className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
      aria-label="Owner insights charts"
    >
      <InsightChartCard title="Guest mix today" icon={Users}>
        <DashboardDonut
          data={guestData}
          centerLabel="Guests"
          emptyLabel="No guests scheduled yet"
        />
        <DashboardDonutLegend data={guestData} />
      </InsightChartCard>

      <InsightChartCard title="Floor status" icon={LayoutGrid}>
        <DashboardDonut
          data={floorData}
          centerLabel="Load"
          centerValue={`${occupancy}%`}
          emptyLabel="No rooms configured"
        />
        <DashboardDonutLegend data={floorData} />
      </InsightChartCard>

      <InsightChartCard title="Appointments" icon={CalendarDays} className="sm:col-span-2 xl:col-span-1">
        <DashboardDonut
          data={apptData}
          centerLabel="Today"
          emptyLabel="No appointments today"
        />
        <DashboardDonutLegend data={apptData} />
      </InsightChartCard>
    </section>
  )
}
